import type { AnalyticsHistory } from "@server/types/analyticsReportTypes";
import {
  ArrowDownLeft,
  ChevronLeft,
  ChevronRight,
  Copy,
  Search,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDuration, statusCodeVariant } from "../utils/formatMetric";

function timestamp(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/** Searches by exact request ID and pages the filtered history; details contain only operational metadata. */
export function RequestExplorer({
  history,
  page,
  isFetching,
  isError,
  requestId,
  onSearch,
  onPage,
}: {
  history: AnalyticsHistory | undefined;
  page: number;
  isFetching: boolean;
  isError: boolean;
  requestId: string;
  onSearch: (value: string) => void;
  onPage: (direction: "previous" | "next") => void;
}) {
  const [search, setSearch] = useState(requestId);
  const rows = history?.requests ?? [];
  return (
    <Card className="analytics-panel">
      <CardHeader className="analytics-explorer-heading">
        <CardTitle>Request history</CardTitle>
        <form
          className="analytics-search"
          onSubmit={(event) => {
            event.preventDefault();
            onSearch(search.trim());
          }}
        >
          <Search size={16} aria-hidden="true" />
          <input
            aria-label="Find request ID"
            placeholder="Find a request ID…"
            maxLength={128}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button type="submit" variant="ghost" size="sm">
            Find
          </Button>
          {requestId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                onSearch("");
              }}
            >
              Clear
            </Button>
          )}
        </form>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Request</TableHead>
              <TableHead>Time (UTC)</TableHead>
              <TableHead>API key</TableHead>
              <TableHead>HTTP status</TableHead>
              <TableHead className="text-right pr-6">Response time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="analytics-empty">
                    <Search size={24} aria-hidden="true" />
                    <strong>
                      {!history
                        ? isError
                          ? "Request history unavailable"
                          : "Loading requests…"
                        : history.nextCursor
                          ? "No matching requests on this page"
                          : "No matching requests"}
                    </strong>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={`${row.timestamp}-${row.requestId}-${index}`}>
                  <TableCell className="pl-6">
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="analytics-request-trigger"
                          aria-label={`View request ${row.requestId ?? index + 1}`}
                        >
                          <span className="analytics-method">
                            {row.method ?? "—"}
                          </span>
                          <span className="min-w-0">
                            <span
                              className="analytics-request-path"
                              title={row.path ?? row.endpoint}
                            >
                              {row.path ?? row.endpoint ?? "Unknown endpoint"}
                            </span>
                            <span className="analytics-request-id">
                              {row.requestId ?? "Request ID unavailable"}
                            </span>
                          </span>
                          <ArrowDownLeft size={14} aria-hidden="true" />
                        </button>
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>Request details</DialogTitle>
                        </DialogHeader>
                        <dl className="analytics-detail">
                          {[
                            ["Request ID", row.requestId ?? "Unavailable"],
                            [
                              "Endpoint",
                              `${row.method ?? ""} ${row.path ?? row.endpoint ?? "Unknown"}`,
                            ],
                            ["Time (UTC)", timestamp(row.timestamp)],
                            ["API key", row.apiKeyName ?? "Unknown"],
                            ["HTTP status", row.statusCode],
                            [
                              "Response time",
                              formatDuration({ ms: row.durationMs }),
                            ],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <dt>{label}</dt>
                              <dd>{value}</dd>
                            </div>
                          ))}
                        </dl>
                        {row.requestId && (
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                if (!row.requestId) return;
                                await navigator.clipboard.writeText(
                                  row.requestId,
                                );
                                toast.success("Request ID copied");
                              } catch {
                                toast.error("Could not copy request ID");
                              }
                            }}
                          >
                            <Copy size={16} />
                            Copy request ID
                          </Button>
                        )}
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {timestamp(row.timestamp)}
                  </TableCell>
                  <TableCell
                    className="max-w-48 truncate"
                    title={row.apiKeyName}
                  >
                    {row.apiKeyName ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={statusCodeVariant({ code: row.statusCode })}
                    >
                      {row.statusCode < 400 ? "Success" : "Error"} ·{" "}
                      {row.statusCode}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right pr-6 tabular-nums">
                    {formatDuration({ ms: row.durationMs })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="analytics-pagination">
          <span>
            {history ? `${rows.length} requests on this page` : ""}
            {history?.scanLimited
              ? " · More activity remains; continue to older requests."
              : ""}
          </span>
          <div>
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous requests"
              disabled={page <= 1 || isFetching}
              onClick={() => onPage("previous")}
            >
              <ChevronLeft size={16} />
            </Button>
            <span>Page {page}</span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next requests"
              disabled={!history?.nextCursor || isFetching}
              onClick={() => onPage("next")}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
